import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, MapPin, Calendar, Check, X, MessageCircle, QrCode } from "lucide-react";
import { STATIC_PACKAGES } from "../constants/staticData";
import { PACKAGE_DETAILS } from "../constants/packageDetails";
import { inr, slug, waLink } from "../constants/site";

export default function PackageDetail() {
  const { slug: pkgSlug } = useParams();
  const pkg = STATIC_PACKAGES.find((p) => slug(p.title) === pkgSlug);
  const detail = PACKAGE_DETAILS[pkgSlug];

  useEffect(() => {
    window.scrollTo(0, 0);
    if (detail) {
      document.title = detail.seoTitle;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute("content", detail.seoDesc);
    }
  }, [pkgSlug, detail]);

  if (!pkg || !detail) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-6 pt-24" data-testid="package-not-found">
        <p className="font-display text-3xl">Package not found</p>
        <Link to="/" className="rounded-full bg-sunset px-6 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-ink">
          Back to all journeys
        </Link>
      </div>
    );
  }

  const bookMsg = `Hi Gautam Travel! I want to book the "${pkg.title}" package (${pkg.duration}, from ${inr(pkg.price)}). Please share availability and next steps.`;

  return (
    <div data-testid="package-detail-page">
      <div className="relative h-[55vh] min-h-[420px] overflow-hidden">
        <img src={pkg.image} alt={pkg.title} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-black/30" />
        <div className="absolute inset-x-0 bottom-0 mx-auto max-w-7xl px-6 pb-12 md:px-10">
          <Link
            to="/"
            data-testid="detail-back-link"
            className="mb-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/70 transition-colors duration-300 hover:text-sunset"
          >
            <ArrowLeft className="h-4 w-4" /> All journeys
          </Link>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-display text-4xl leading-tight sm:text-5xl lg:text-6xl"
            data-testid="detail-title"
          >
            {pkg.title}
          </motion.h1>
          <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/70">
            <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-sunset" />{pkg.destination}</span>
            <span className="flex items-center gap-2"><Clock className="h-4 w-4 text-teal" />{pkg.duration}</span>
            <span className="flex items-center gap-2"><Calendar className="h-4 w-4 text-coral" />Best time: {detail.bestTime}</span>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 py-16 md:px-10 md:py-20">
        <div className="grid gap-14 lg:grid-cols-[1fr_380px]">
          <div>
            <p className="max-w-3xl text-base font-light leading-relaxed text-white/70 md:text-lg" data-testid="detail-overview">
              {detail.overview}
            </p>

            <h2 className="mt-14 font-display text-3xl">Day-by-day itinerary</h2>
            <div className="mt-8 space-y-0" data-testid="detail-itinerary">
              {detail.days.map((d, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.6 }}
                  className="relative flex gap-6 border-l border-white/15 pb-10 pl-8 last:pb-0"
                  data-testid={`detail-day-${i + 1}`}
                >
                  <span className="absolute -left-[17px] top-0 flex h-8 w-8 items-center justify-center rounded-full border border-sunset/50 bg-ink text-xs font-semibold text-sunset">
                    {i + 1}
                  </span>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-white/40">Day {i + 1}</p>
                    <h3 className="mt-1 font-display text-xl">{d.title}</h3>
                    <p className="mt-2 text-sm font-light leading-relaxed text-white/60">{d.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="mt-14 grid gap-8 sm:grid-cols-2">
              <div className="rounded-3xl border border-teal/20 bg-teal/[0.05] p-7" data-testid="detail-inclusions">
                <h3 className="font-display text-xl text-teal">What's included</h3>
                <ul className="mt-4 space-y-2.5">
                  {detail.inclusions.map((x) => (
                    <li key={x} className="flex items-start gap-2.5 text-sm font-light text-white/70">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-teal" /> {x}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-7" data-testid="detail-exclusions">
                <h3 className="font-display text-xl text-white/70">Not included</h3>
                <ul className="mt-4 space-y-2.5">
                  {detail.exclusions.map((x) => (
                    <li key={x} className="flex items-start gap-2.5 text-sm font-light text-white/50">
                      <X className="mt-0.5 h-4 w-4 shrink-0 text-coral/70" /> {x}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <h2 className="mt-14 font-display text-3xl">Glimpses</h2>
            <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3" data-testid="detail-gallery">
              {detail.gallery.map((src, i) => (
                <div key={i} className={`overflow-hidden rounded-2xl border border-white/10 ${i === 0 ? "col-span-2 md:col-span-1" : ""}`}>
                  <img src={src} alt={`${pkg.title} ${i + 1}`} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-105" />
                </div>
              ))}
            </div>
          </div>

          <aside className="lg:sticky lg:top-28 lg:self-start">
            <div className="rounded-3xl border border-white/10 bg-surface/80 p-8 backdrop-blur-xl" data-testid="detail-booking-card">
              <p className="text-[10px] uppercase tracking-[0.3em] text-white/40">Starting from</p>
              <p className="mt-2 font-display text-5xl text-sunset">{inr(pkg.price)}</p>
              <p className="mt-1 text-xs text-white/50">per person · {pkg.duration}</p>
              <p className="mt-4 text-xs text-white/50">Departures: {detail.departure}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {pkg.highlights.map((h) => (
                  <span key={h} className="rounded-full border border-white/10 px-3 py-1 text-[11px] text-white/60">{h}</span>
                ))}
              </div>
              <a
                href={waLink(bookMsg)}
                target="_blank"
                rel="noreferrer"
                data-testid="detail-book-whatsapp-btn"
                className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-sunset py-4 text-xs font-semibold uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-coral"
              >
                <MessageCircle className="h-4 w-4" /> Book on WhatsApp
              </a>
              <Link
                to="/#pay"
                data-testid="detail-pay-link"
                className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full border border-white/15 py-4 text-xs font-medium uppercase tracking-[0.2em] text-white/80 transition-colors duration-300 hover:border-teal hover:text-teal"
              >
                <QrCode className="h-4 w-4" /> Pay via UPI QR
              </Link>
              <p className="mt-5 text-center text-[11px] leading-relaxed text-white/35">
                Every itinerary is customisable — tell us your dates & budget and we reshape it around you.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
